import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { buildUrlWithParams, get, pageContext } from 'ctm-util-functions';
import { quoteIdSelector, resultsConfigSelector, sortedAndFilteredProductsSelector, tokensSelector } from 'ctm-selectors';
import { connect } from 'react-redux';

import { WizardModal } from '@comparethemarketau/manor-modal';
import { Carousel } from '@comparethemarketau/manor-carousel';
import { Typography } from '@comparethemarketau/manor-typography';
import { Picture } from '@comparethemarketau/manor-picture';
import { Link } from '@comparethemarketau/manor-link';
import { Row, Column, Container } from '@comparethemarketau/manor-grid';
import { Button as ManorButton } from '@comparethemarketau/manor-button';
import { faAngleRight, faAngleLeft } from '@fortawesome/pro-regular-svg-icons';

import styles from './insurer_handover_button.css';
import { Button } from '../button/button';

/**
 * Renders an InsurerHandoverButton Component with a url with parameters passed in from stateProps
 * @param {Object} props React props
 * @returns {_InsurerHandoverButton} a _InsurerHandoverButton component
 */
const _InsurerHandoverButton = (props) => {
    const {
        journeyId,
        quoteId,
        productId,
        accessToken,
        productPosition,
        paymentFrequency,
        api,
        dispatch,
        stateProps,
        href,
        vertical,
        label,
        offerDetails,
        ...buttonProps
    } = props;
    const urlParams = {
        load_journey: 'true',
        journey_id: journeyId,
        quote_id: quoteId,
        product_id: productId,
        product_position: productPosition,
        payment_frequency: paymentFrequency,
        access_token: accessToken
    };

    if (pageContext.getProps().everestTheme === 'choo' && buttonProps && buttonProps.UIComponent) {
        buttonProps.UIComponent = `${buttonProps.UIComponent}_choo`;
    }

    let hrefWithParams = buildUrlWithParams(href, urlParams);

    if (offerDetails && offerDetails.length > 0) {
        const updatedURLParams = { ...urlParams, offerShown: true };
        hrefWithParams = buildUrlWithParams(href, updatedURLParams);
    }

    // only for KOBA now (they are off the panel for now)
    if  (label.includes('KOBA') && vertical === 'car-insurance') {
        const ctmChatButton = document.getElementById('ctmChatButton');
        const [visible, setVisible] = useState(false);
        const [carouselIndex, setCarouselIndex] = useState(0);
        const { product } = stateProps;
        const productDisclosures = product.getIn(['productMeta', 'productDisclosures']);
        const pages = getPages(productDisclosures, label);

        const handleComplete = () => {
            closeModal();
        };

        const openModal = () => {
            setVisible(true);
            ctmChatButton.style.visibility = 'hidden';
        };

        const closeModal = () => {
            setVisible(false);
            ctmChatButton.style.visibility = 'visible';
        };

        const handleNext = () => {
            setCarouselIndex(carouselIndex + 1);
        };

        const handlePrevious = () => {
            setCarouselIndex(carouselIndex - 1);
        };

        const carouselActions = (pageCount) => {
            const leftButton = carouselIndex === 0 ? (
                <div style={{ width: '1rem' }} />
            ) : (
                <ManorButton
                    id="Previous-btn"
                    variant="tertiary"
                    className={styles.modalPreviousButton}
                    handleClick={handlePrevious}
                    icon={faAngleLeft}
                    trackingLabel={'Previous button for hand-over-tracking'}
                >
                    Previous
                </ManorButton>
            );

            const rightButton = carouselIndex === pageCount - 1 ? (
                <ManorButton
                    id="Complete-btn"
                    variant="hero"
                    className={styles.modalCompleteButton}
                    handleClick={handleComplete}
                    href={hrefWithParams}
                    icon={faAngleRight}
                    iconAlign="right"
                    trackingLabel={'Complete button for hand-over-tracking'}
                >
                    I Agree and Continue
                </ManorButton>
            ) : (
                <ManorButton
                    id="next-btn"
                    variant="primary"
                    className={styles.modalNextButton}
                    handleClick={handleNext}
                    icon={faAngleRight}
                    iconAlign="right"
                    trackingLabel={'Next button for hand-over-tracking'}
                >
                    Next
                </ManorButton>
            );

            return (
                <div className={styles.modalActions}>
                    <div>{leftButton}</div>
                    <div>{rightButton}</div>
                </div>
            );
        };

        return (
            <>
                <Button {...buttonProps} label={label} className={styles.root} onClick={openModal} stateProps={stateProps} />
                <WizardModal
                    id="handover-wizard"
                    trackingLabel="handover-wizard-tracking"
                    visible={visible}
                    onClose={closeModal}
                >
                    <Carousel
                        id="handover-wizard-carousel"
                        trackingLabel="handover-wizard-carousel-tracking"
                        onActiveIndexChanged={setCarouselIndex}
                        carouselIndex={carouselIndex}
                    >
                        {pages}
                    </Carousel>
                    {carouselActions(pages.length)}
                </WizardModal>
            </>
        );
    }

    return <Button {...buttonProps} label={label} className={styles.root} href={hrefWithParams} />;
};

_InsurerHandoverButton.propTypes = {
    label: PropTypes.string,
    icon: PropTypes.string,
    iconPosition: PropTypes.oneOfType([PropTypes.oneOf(['before', 'after'])]),
    href: PropTypes.string.isRequired,
    target: PropTypes.string,
    variant: PropTypes.string,
    UIComponent: PropTypes.string,
    // eslint-disable-next-line react/no-unused-prop-types
    stateProps: PropTypes.shape({
        product: PropTypes.object.isRequired
    }),
    api: PropTypes.object,
    dispatch: PropTypes.func,
    productId: PropTypes.string,
    journeyId: PropTypes.number,
    quoteId: PropTypes.number,
    accessToken: PropTypes.string,
    productPosition: PropTypes.number,
    paymentFrequency: PropTypes.string,
    vertical: PropTypes.string
};

/**
 * Get journey and quote info from state to form the url query strings
 * @param {Immutable.Map} state - Redux store state
 * @param {Object} props - component's props
 * @returns {Object} - props that needed for build url query strings
 */
export const mapStateToProps = (state, props) => {
    const product = get(props, 'stateProps.product');
    return {
        journeyId: state.getIn(['user', 'journey', 'transaction', 'journeyId']),
        quoteId: quoteIdSelector(state),
        productId: product.getIn(['productMeta', 'id']),
        accessToken: tokensSelector(state).get('access_token'),
        productPosition: sortedAndFilteredProductsSelector(state).indexOf(product) + 1,
        paymentFrequency: resultsConfigSelector(state).getIn(['filters', 'frequency']),
        vertical: state.getIn(['page', 'vertical']),
        offerDetails: state.getIn(['user', 'journey', 'offers'])
    };
};

const InsurerHandoverButton = connect(mapStateToProps)(_InsurerHandoverButton);
export { _InsurerHandoverButton, InsurerHandoverButton };

const getPicture = function (title, filename) {
    const src = `/evt/assets/car-insurance/images/logos/car-insurance/${filename}`;
    return <Picture alt={title} title={title} src={src} />;
};

const getPages = function (productDisclosures, label) {
    const pdsObject = productDisclosures.find((element) => element.get('code') === 'PDS');
    const pegObject = productDisclosures.find((element) => element.get('code') === 'PEG');
    const carpeeshPolicyDocumentsURL = 'https://carpeesh.com/policy-documents';
    const brand = label;

    const { breakpoint } = pageContext.getProps().pageState;

    let titleStyle = { fontSize: '2.4rem', fontWeight: '600' };
    let contentPaddingSpace = ['28', '84', '28', '84'];
    if (breakpoint === 'xs') {
        contentPaddingSpace = ['24', '36', '24', '36'];
        titleStyle = { fontSize: '2rem', fontWeight: '600' };
    } else if (breakpoint === 'sm') {
        contentPaddingSpace = ['28', '60', '28', '64'];
        titleStyle = { fontSize: '2.4rem', fontWeight: '600' };
    }

    return { 'Go to Carpeesh': [
        <Container padding={contentPaddingSpace} key={'page-1'}>
            <Row>
                <Column valign="center" halign="center">
                    {getPicture('contract', 'contract-info.svg')}
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        Introducing the Driver Safety App
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        To use the features of this product you will be required to download Carpeesh’s
                        {' '}
                        <strong>Driver Safety App</strong>
                        {' '}
                        on your mobile phone
                        within 14 days from the beginning of the period of insurance.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        Where the App is not downloaded within 14 days from the beginning of the period of insurance,
                        {' '}
                        <strong>an additional excess of $1,000 will apply on any claim.</strong>
                    </Typography>
                </Column>
            </Row>
        </Container>,
        <Container padding={contentPaddingSpace} key={'page-2'}>
            <Row>
                <Column valign="center" halign="center">
                    {getPicture('driver app', 'driver-app.svg')}
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        How It Works
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        The Driver Safety App samples your driving data; how far and how safely you drive your car impacts the future premiums you pay,
                        {' '}
                        with good drivers of any age rewarded and unsafe drivers required to pay more.
                    </Typography>
                </Column>
            </Row>
        </Container>,
        <Container padding={contentPaddingSpace} key={'page-3'}>
            <Row>
                <Column valign="center" halign="center">
                    {getPicture('reminders', 'reminders.svg')}
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        Cooling Off Period
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        This Policy has a 14-day cooling off period. Cancellations after the 14-day cooling off period (but before the expiry date of the
                        policy) will incur a cancellation fee of $50 (plus GST). There is no refund on the one-off Driver Safety App Fee initially charged as
                        part of your premium.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        Please refer to the
                        {' '}
                        <Link trackingLabel="PDS link" href={carpeeshPolicyDocumentsURL} target="_blank" underline="none">
                            Product Disclosure Statement,
                        </Link>
                        {' '}
                        and
                        {' '}
                        <Link trackingLabel="SPDS link" href={carpeeshPolicyDocumentsURL} target="_blank" underline="none">
                            Supplementary Product Disclosure Statement
                        </Link>
                        {' '}
                        and
                        {' '}
                        <Link trackingLabel="PEG link" href={carpeeshPolicyDocumentsURL} target="_blank" underline="none">
                            Premium and Excess Guide
                        </Link>
                        {' '}
                        for full details.
                    </Typography>
                </Column>
            </Row>
        </Container>
    ],
    'Go to KOBA': [
        <Container padding={contentPaddingSpace} key={'page-1'}>
            <Row>
                <Column valign="center" halign="center">
                    {getPicture('contract', 'contract-info.svg')}
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        KOBA’s pay-per-km insurance
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        KOBA offers the same type of comprehensive car insurance as other insurers; but with a new way to pay.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        To use the features of this product, you will need to download KOBA's smartphone app and plug a KOBA Rider (a matchbox-sized device) into your car.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        This device will be sent to you when you buy a policy.
                    </Typography>
                </Column>
            </Row>
        </Container>,
        <Container padding={contentPaddingSpace} key={'page-2'}>
            <Row>
                <Column valign="center" halign="center">
                    {getPicture('payment', 'payment.svg')}
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        Understanding your quote
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        You pay for cover with KOBA in the following way:
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        {' '}
                        <strong>An upfront fixed cost</strong>
                        {' '}
                        which covers your car while it’s not in use and a small Admin fee. Both are refundable on a pro-rata basis, if the policy is cancelled during the term;
                        {' '}
                        <strong>PLUS</strong>
                        {' '}
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        {' '}
                        <strong>A Per-km rate</strong>
                        {' '}
                        which covers you while driving and is used to calculate your monthly Driving premium which is payable at the end of
                        each month based on the total kilometres driven as measured through your KOBA Rider.
                    </Typography>
                </Column>
            </Row>
        </Container>,
        <Container padding={contentPaddingSpace} key={'page-3'}>
            <Row>
                <Column valign="center" halign="center">
                    {getPicture('driver app', 'driver-app.svg')}
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        How It Works
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        The KOBA Rider securely measures the number of kilometres you drive; the less you drive each month the lower the premium.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        Through your App, you will be able to see real time trips, monthly costs, claims information and policy documents.
                    </Typography>
                </Column>
            </Row>
        </Container>,
        <Container padding={contentPaddingSpace} key={'page-4'}>
            <Row>
                <Column valign="center" halign="center">
                    {getPicture('reminders', 'reminders.svg')}
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        Additional information
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        You have 14 days from your policy start date to plug in your KOBA Rider.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        KOBA policies rely on the information from the KOBA Rider so if you don’t plug in the Rider device within 14 days,
                        you may be charged for the maximum estimated daily kilometres of 250km per day.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        For more details about this product please refer to
                        {' '}
                        <Link trackingLabel="PDS link" href={pdsObject.get('url')} target="_blank" underline="none">
                            Product Disclosure Statement
                        </Link>
                        {' '}
                        &
                        {' '}
                        <Link trackingLabel="PEG link" href="https://www.kobainsurance.com.au/TMD" target="_blank" underline="none">
                            Target Market Determination
                        </Link>
                        {' '}
                    </Typography>
                </Column>
            </Row>
        </Container>
    ] }[brand];
};
